import { RefObject, useEffect, useState } from 'react';
import React from 'react';
import ReactQuill, { Quill } from 'react-quill';
import Card from 'ui/views/cards/Card';
import { ContentAPIUrls } from 'apis/CompanyAPI/content/UpdateAPI';
import { EmbeddedUpdateDTO } from 'types/content/embeddedUpdate';
import { asCompanyAuthor, asUserAuthor, AuthorImage } from 'domain/shared/author';
import { communityUrls, companyUrls } from 'urls';
import classNames from 'classnames';
import { richTextContentPreviewParser } from 'domain/Updates/shared/utils';
import { timeSince } from 'util/dateUtils';
import useRoute from 'hooks/useRoute';
import themeBluePlanet from 'ui/theme/themeBluePlanet';
import { contentSpacing } from 'ui/theme/themeConstants';
import useResource from 'util/resource/useResource';
import Resource from 'util/resource/Resource';
import HtmlContentWithMentions from 'domain/shared/HtmlContentWithMentions';

export const POST_BLOT_CLASS = 'ql-post-embed';

export function stripUpdateEmbeds(rawHtml: string) {
  const container = document.createElement('div');
  container.innerHTML = rawHtml;
  container.querySelectorAll('.' + POST_BLOT_CLASS).forEach(blot => {
    blot.innerHTML = '';
  });
  return container.innerHTML;
}

// Quill blot definition without inner mount wrapper
const BlockEmbed = Quill.import('blots/block/embed');
export interface EmbeddedUpdate {
  updateId: string;
  location: { type: 'company' | 'community'; slug: string };
}
export class PostBlot extends BlockEmbed {
  static blotName = 'postBlot';
  static tagName = 'div';
  static className = POST_BLOT_CLASS;

  static create(value: EmbeddedUpdate) {
    const node = super.create() as HTMLElement;
    node.setAttribute('contenteditable', 'false');
    node.dataset.updateId = value.updateId;
    node.dataset.type = value.location.type;
    node.dataset.slug = value.location.slug;
    return node;
  }

  static value(node: HTMLElement) {
    return {
      updateId: node.dataset.updateId!,
      location: { type: node.dataset.type as any, slug: node.dataset.slug! },
    };
  }
}
Quill.register('formats/postBlot', PostBlot);

// Hook to collect blot nodes for portal mounting
interface MountPoint {
  node: HTMLElement;
  updateId: string;
  slug: string;
  type: 'company' | 'community';
}
export function useHydrateUpdateBlots(ref: RefObject<ReactQuill> | RefObject<HTMLElement>) {
  const [mounts, setMounts] = useState<MountPoint[]>([]);

  useEffect(() => {
    const current = ref.current;
    if (!current) return;

    const root: HTMLElement =
      'getEditor' in current ? (current.getEditor().root as HTMLElement) : (current as HTMLElement);

    const scan = () => {
      const nodes = Array.from(root.querySelectorAll(`.${POST_BLOT_CLASS}`)) as HTMLElement[];

      setMounts(
        nodes
          .filter(n => n.dataset.updateId)
          .map(n => ({
            node: n,
            updateId: n.dataset.updateId!,
            slug: n.dataset.slug!,
            type: n.dataset.type as any,
          })),
      );
    };

    scan();
    const observer = new MutationObserver(scan);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [ref]);

  return mounts;
}

// Embed component remains unchanged
interface Props {
  updateId: string;
  slug: string;
  type: 'company' | 'community';
}

const MAX_NESTING_LEVEL = 3;

export function isNestingTooDeep(element: HTMLElement): boolean {
  const nestingLevel = countParentPostBlots(element);
  return nestingLevel === -1;
}

export function countParentPostBlots(element: HTMLElement): number {
  const hasPostBlotClass = (el: HTMLElement | null): boolean => el?.classList.contains(POST_BLOT_CLASS) ?? false;

  const countInAncestors = (current: HTMLElement | null, count: number): number => {
    if (!current || count > MAX_NESTING_LEVEL) return count > MAX_NESTING_LEVEL ? -1 : count;
    return countInAncestors(current.parentElement, hasPostBlotClass(current) ? count + 1 : count);
  };

  return countInAncestors(element, 0);
}

export function UpdateEmbed({ updateId, slug, type }: Props) {
  const [isTooDeeplyNested, setIsTooDeeplyNested] = React.useState(false);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const updateUrl =
    slug && type
      ? type === 'company'
        ? companyUrls.update.view(slug, updateId)
        : communityUrls.update.view(slug, updateId)
      : undefined;

  const { resource: updateResource } = useResource<EmbeddedUpdateDTO>(ContentAPIUrls.getUpdateForEmbed(updateId));

  useEffect(() => {
    if (cardRef.current) {
      setIsTooDeeplyNested(isNestingTooDeep(cardRef.current));
    }
  }, []);

  const { push } = useRoute();

  if (isTooDeeplyNested) {
    return (
      <Card elevation={3} style={{ borderRadius: 4, marginBottom: contentSpacing }}>
        <div className="text-grey text italic">This content is unavailable</div>
      </Card>
    );
  }

  return (
    <Card
      ref={cardRef}
      onClick={() => updateUrl && push(updateUrl)}
      elevation={3}
      style={{ borderRadius: 4, cursor: updateUrl ? 'pointer' : 'default', marginBottom: contentSpacing }}
    >
      <Resource
        resource={updateResource}
        renderError={() => <div className="text-grey text italic">This content is unavailable</div>}
        renderLoading={() => (
          <div
            className="u-fade-in"
            style={{ height: 100, borderRadius: 4, backgroundColor: themeBluePlanet.bluePlanetPalette.grey.medium }}
          />
        )}
      >
        {update => {
          const author = update.postedAs
            ? asCompanyAuthor(
                { ...update.author, position: update.author.title },
                { ...update.postedAs, logoURL: update.postedAs.logoUrl },
              )
            : asUserAuthor({ ...update.author, position: update.author.title });

          return (
            <>
              <div className="u-flex u-flex--gap-half u-content-spacing-bottom u-fade-in">
                <AuthorImage size={44} author={author} />
                <div className="text-small u-flex u-flex--column u-flex-center" style={{ lineHeight: 1.33 }}>
                  <div>{author?.type === 'company' ? 'By ' + author.company?.name : author?.name}</div>
                  <div className="text-grey">
                    {author?.type === 'company' ? author.user?.name : author?.position}
                    {update.author.title ? ' • ' : ''}
                    {timeSince(update.createdAt)}
                  </div>
                </div>
              </div>
              {update.content ? (
                <HtmlContentWithMentions
                  className={classNames('ql-rich-text', 'u-content-spacing-bottom', 'data-content-read-more')}
                  style={{ pointerEvents: 'none' }}
                >
                  {richTextContentPreviewParser(update.content, 100, 0).previewHtmlString}
                </HtmlContentWithMentions>
              ) : (
                <div className={classNames('u-content-spacing-bottom', 'text-grey', 'text-italic')}>
                  This content is unavailable
                </div>
              )}
            </>
          );
        }}
      </Resource>
    </Card>
  );
}
